import { type ReactElement, useCallback, useEffect, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import cx from "classnames";

import { getThrottledOnClick } from "@olivahealth/utils/helpers/core/element";
import useDebounce from "@olivahealth/utils/reactHooks/useDebounce";
import type { Question } from "../../types/Question";
import Text from "../../atoms/Text";
import Checkbox from "../../molecules/Checkbox";
import * as s from "../../shared/surveyStyles";
import { DEBOUNCE_TEXT_TIME } from "../TextQuestion";

export type CheckboxQuestionVariant = "primary" | "secondary" | "dark";

interface Props {
  isDisabled?: boolean;
  onChange?: (value: string[]) => void;
  question: Question;
  selected?: string[] | undefined;
  selectedSubValues?: Record<string, string> | null;
  handleSubValueChange?: (optionValue: string, subValue: string) => void;
  subValuePlaceholder?: string;
  variant?: CheckboxQuestionVariant;
  renderInRow?: boolean;
  showCheckbox?: boolean;
}

export default function CheckboxQuestion({
  isDisabled,
  onChange,
  question,
  selected,
  handleSubValueChange,
  selectedSubValues,
  subValuePlaceholder,
  variant = "primary",
  renderInRow = false,
  showCheckbox = false,
}: Readonly<Props>): ReactElement | null {
  const [selectedItems, setSelectedItems] = useState<Set<string>>(
    new Set(selected?.map((item) => item.toString()) || []),
  );

  const handleOnChange = useCallback(
    (value: string) => {
      const newSelectedItems = new Set(selectedItems);

      if (newSelectedItems.has(value)) {
        newSelectedItems.delete(value);
      } else {
        newSelectedItems.add(value);
      }

      setSelectedItems(newSelectedItems);
      onChange?.(Array.from(newSelectedItems));
    },
    [selectedItems, onChange],
  );

  useEffect(() => {
    setSelectedItems(new Set(selected?.map((item) => item.toString()) || []));
  }, [selected]);

  const [subValueTexts, setSubValueTexts] = useState<Record<string, string>>(
    selectedSubValues || {},
  );
  const debouncedSubValueTexts = useDebounce(subValueTexts, DEBOUNCE_TEXT_TIME);

  useEffect(() => {
    if (!handleSubValueChange) {
      return;
    }

    Object.entries(debouncedSubValueTexts).forEach(
      ([optionValue, subValue]) => {
        if (subValue !== undefined && selectedItems.has(optionValue)) {
          handleSubValueChange(optionValue, subValue);
        }
      },
    );
  }, [debouncedSubValueTexts, selectedItems, handleSubValueChange]);

  const handleSubValueTextChange = useCallback(
    (optionValue: string, value: string) => {
      setSubValueTexts((prev) => ({
        ...prev,
        [optionValue]: value,
      }));
    },
    [],
  );

  if (!question.options?.length) {
    return null;
  }

  // Map variant to checkbox variant
  const getCheckboxVariant = () => {
    if (variant === "dark") return "white";
    return "default";
  };

  return (
    <>
      {question.question && (
        <Text
          variant="h3"
          component="h1"
          color={variant === "dark" ? "white" : "accent-primary"}
          gutter="md"
        >
          <span
            dangerouslySetInnerHTML={{
              __html: question.question,
            }}
          />
        </Text>
      )}
      <div className={renderInRow ? s.optionGroupRow : s.optionGroup}>
        {question.options.map((option) => {
          const onClick = () => handleOnChange(option.value);
          const throttledOnClick = getThrottledOnClick(onClick);
          const hasSubValue = (option.subValue?.length ?? -1) >= 0;
          const isSelected = selectedItems.has(option.value);

          return (
            <button
              key={option.id}
              value={option.value}
              className={(() => {
                const isRow = renderInRow ? "flex-1" : "";

                if (variant === "secondary") {
                  return `${s.optionSecondary} ${isSelected && s.optionSecondarySelected} ${isRow}`;
                }

                if (variant === "dark") {
                  return `${s.optionDark} ${isSelected && s.optionDarkSelected} ${isRow}`;
                }

                return `${s.option} ${isSelected && s.optionSelected} ${isRow}`;
              })()}
              onClick={throttledOnClick}
              disabled={isDisabled}
            >
              <div
                className={cx(s.optionsContainer, {
                  "flex-col items-baseline": hasSubValue,
                })}
              >
                <div className="flex items-center">
                  {showCheckbox && (
                    <div className="flex-shrink-0 mr-3">
                      <Checkbox
                        name={`checkbox-${option.value}`}
                        label=""
                        value={option.value}
                        checked={isSelected}
                        disabled={isDisabled}
                        variant={getCheckboxVariant()}
                        width="auto"
                        changeValueOnLabelClick={false}
                        onChange={() => {}} // Handled by button onClick
                      />
                    </div>
                  )}
                  {option.icon && <span className="mr-3">{option.icon}</span>}
                  <div className="flex flex-col">
                    <label className={s.optionLabel}>{option.label}</label>
                    {option.sublabel && (
                      <div
                        className={cx("text-sm text-gray-500 text-left", {
                          ["hidden"]: hasSubValue && !isSelected,
                          "block mb-2 mt-3": hasSubValue && isSelected,
                        })}
                      >
                        {option.sublabel}
                      </div>
                    )}
                  </div>
                </div>
                {hasSubValue && isSelected ? (
                  <TextareaAutosize
                    autoFocus
                    value={subValueTexts[option.value] || ""}
                    name={`${question.id}-${option.value}`}
                    id={`${question.id}-${option.value}`}
                    minRows={1}
                    maxRows={1}
                    placeholder={subValuePlaceholder}
                    onChange={(event) => {
                      handleSubValueTextChange(
                        option.value,
                        event.target.value,
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key === " ") {
                        handleSubValueTextChange(
                          option.value,
                          `${subValueTexts[option.value] || ""} `,
                        );
                      }
                    }}
                    disabled={isDisabled}
                    className={s.textarea}
                  />
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
